import API_BASE from "./apiConfig";
import React, { useState, useEffect, useMemo } from "react";
import {
  FaSearch, FaChartLine, FaTrophy, FaUser, FaTimes,
  FaBaseballBall, FaBullseye, FaShieldAlt, FaStar,
  FaFilter, FaFire, FaMedal, FaCalendarAlt, FaVenusMars,
  FaIdCard, FaCommentDots, FaExclamationCircle,
  FaDumbbell, FaHandPaper, FaBrain, FaRunning, FaUsers,
} from "react-icons/fa";
import { Colors } from "./branding";
import { useAppConfig } from "./AppContext";
import { useTheme, getT, glassCard } from "./ThemeContext";

const BASE = API_BASE;
// ─── Design tokens — IDENTICAL to ParentDashboard ────────────────────────────
const T = {
  glass:      "rgba(255,255,255,0.72)",
  glassBdr:   "1px solid rgba(255,255,255,0.55)",
  glassSh:    "0 4px 24px rgba(0,40,100,0.09), 0 1px 4px rgba(0,40,100,0.05)",
  blur:       "blur(18px)",
  yellow:     Colors.logoYellow  || "#f7ce0b",
  blue:       Colors.primaryBlue || "#034e7b",
  dark:       "#02304f",
  mid:        "#045a8d",
  gray:       "#8899aa",
  lightGray:  "#f0f4f8",
  inputBg:    "rgba(255,255,255,0.88)",
  font:       "'Segoe UI', system-ui, sans-serif",
  topBar:     "linear-gradient(135deg, #02304f 0%, #034e7b 45%, #045a8d 75%, #0369a1 100%)",
  pageBg:     "linear-gradient(160deg, #e8f0f8 0%, #dce8f2 50%, #e4ecf5 100%)",
};

// Glass card base — IDENTICAL to ParentDashboard
// gc is now computed per-component via glassCard(T)

// ─── Category colours — IDENTICAL to ParentDashboard ─────────────────────────
const CAT_COLORS = [
  { color:"#f59e0b", grad:"linear-gradient(135deg,#fbbf24,#f59e0b)", lightBg:"rgba(245,158,11,0.08)",  border:"rgba(245,158,11,0.22)"  },
  { color:"#3b82f6", grad:"linear-gradient(135deg,#60a5fa,#3b82f6)", lightBg:"rgba(59,130,246,0.08)",  border:"rgba(59,130,246,0.22)"  },
  { color:"#10b981", grad:"linear-gradient(135deg,#34d399,#10b981)", lightBg:"rgba(16,185,129,0.08)",  border:"rgba(16,185,129,0.22)"  },
  { color:"#8b5cf6", grad:"linear-gradient(135deg,#a78bfa,#8b5cf6)", lightBg:"rgba(139,92,246,0.08)",  border:"rgba(139,92,246,0.22)"  },
  { color:"#ec4899", grad:"linear-gradient(135deg,#f472b6,#ec4899)", lightBg:"rgba(236,72,153,0.08)",  border:"rgba(236,72,153,0.22)"  },
  { color:"#14b8a6", grad:"linear-gradient(135deg,#2dd4bf,#14b8a6)", lightBg:"rgba(20,184,166,0.08)",  border:"rgba(20,184,166,0.22)"  },
  { color:"#f97316", grad:"linear-gradient(135deg,#fb923c,#f97316)", lightBg:"rgba(249,115,22,0.08)",  border:"rgba(249,115,22,0.22)"  },
  { color:"#6366f1", grad:"linear-gradient(135deg,#818cf8,#6366f1)", lightBg:"rgba(99,102,241,0.08)",  border:"rgba(99,102,241,0.22)"  },
  { color:"#06b6d4", grad:"linear-gradient(135deg,#22d3ee,#06b6d4)", lightBg:"rgba(6,182,212,0.08)",   border:"rgba(6,182,212,0.22)"   },
];

const FIXED_META = {
  Batting:  { icon:FaBaseballBall, ...CAT_COLORS[0] },
  Bowling:  { icon:FaBullseye,     ...CAT_COLORS[1] },
  Fielding: { icon:FaShieldAlt,    ...CAT_COLORS[2] },
};

function getCatMeta(name, idx) {
  if (FIXED_META[name]) return FIXED_META[name];
  const extras = {
    "Fitness":                   { icon:FaDumbbell,  ...CAT_COLORS[3] },
    "Wicketkeeping":             { icon:FaHandPaper, ...CAT_COLORS[4] },
    "Game Awareness & IQ":       { icon:FaBrain,     ...CAT_COLORS[5] },
    "Mental Skills or Attitude": { icon:FaStar,      ...CAT_COLORS[6] },
    "Agility & Movement":        { icon:FaRunning,   ...CAT_COLORS[7] },
    "Teamwork & Communication":  { icon:FaUsers,     ...CAT_COLORS[8] },
  };
  if (extras[name]) return extras[name];
  return { icon:FaStar, ...CAT_COLORS[idx % CAT_COLORS.length] };
}

// ─── Timeframes — IDENTICAL to ParentDashboard ───────────────────────────────
const TIMEFRAMES = [
  { id:"30d",    label:"30 Days", days:30   },
  { id:"60d",    label:"60 Days", days:60   },
  { id:"90d",    label:"90 Days", days:90   },
  { id:"1y",     label:"1 Year",  days:365  },
  { id:"all",    label:"All",     days:null },
  { id:"custom", label:"Custom",  days:null },
];

function filterByTimeframe(trend, tfId, customFrom, customTo) {
  if (!trend || !trend.length) return [];
  if (tfId === "all") return trend;
  const now = new Date();
  let from, to;
  if (tfId === "custom") {
    if (!customFrom) return trend;
    from = new Date(customFrom);
    to   = customTo ? new Date(customTo) : now;
  } else {
    const tf = TIMEFRAMES.find(t => t.id === tfId);
    to = now; from = new Date(now);
    from.setDate(from.getDate() - tf.days);
  }
  return trend.filter(t => { const d = new Date(t.date); return d >= from && d <= to; });
}

function useTimeframe() {
  const [tfId,       setTfId]       = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo,   setCustomTo]   = useState("");
  return { tfId, setTfId, customFrom, setCustomFrom, customTo, setCustomTo };
}

// ─── TfPill — IDENTICAL to ParentDashboard ───────────────────────────────────
function TfPill({ tf }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  return (
    <div style={{ display:"flex", alignItems:"center", gap:4, flexWrap:"wrap" }}>
      <div style={{ display:"flex", alignItems:"center", gap:3, padding:"3px 8px", borderRadius:20, background:T.inputBg, border:`1px solid ${T.inputBdr}` }}>
        <FaFilter size={7} style={{ color:T.blue }}/>
        <select value={tf.tfId} onChange={e=>tf.setTfId(e.target.value)}
          style={{ border:"none", outline:"none", background:"transparent", fontSize:10, fontWeight:700, color:T.inputText, fontFamily:T.font, cursor:"pointer", WebkitTapHighlightColor:"transparent", colorScheme: isDark ? "dark" : "light" }}>
          {TIMEFRAMES.map(t=><option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
      </div>
      {tf.tfId==="custom" && (
        <>
          <input type="date" value={tf.customFrom} onChange={e=>tf.setCustomFrom(e.target.value)}
            style={{ fontSize:10, padding:"3px 6px", borderRadius:6, border:`1px solid ${T.inputBdr}`, fontFamily:T.font, color:T.inputText, background:T.inputBg, width:100, colorScheme: isDark ? "dark" : "light" }}/>
          <span style={{ fontSize:9, color:T.gray }}>→</span>
          <input type="date" value={tf.customTo} onChange={e=>tf.setCustomTo(e.target.value)}
            style={{ fontSize:10, padding:"3px 6px", borderRadius:6, border:`1px solid ${T.inputBdr}`, fontFamily:T.font, color:T.inputText, background:T.inputBg, width:100, colorScheme: isDark ? "dark" : "light" }}/>
        </>
      )}
    </div>
  );
}

// ─── SectionLabel — IDENTICAL to ParentDashboard ─────────────────────────────
function SectionLabel({ children, right }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  return (
    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
      <div style={{ height:2, width:12, background:T.yellow, borderRadius:2, flexShrink:0 }}/>
      <span style={{ fontSize:10, fontWeight:800, letterSpacing:"0.10em", textTransform:"uppercase", color:T.textLabel, fontFamily:T.font, whiteSpace:"nowrap" }}>{children}</span>
      <div style={{ flex:1, height:1, background:T.divider }}/>
      {right && <div style={{ flexShrink:0 }}>{right}</div>}
    </div>
  );
}

// ─── TriangleRadar — IDENTICAL to ParentDashboard ────────────────────────────
function TriangleRadar({ data, size=280 }) {
  if (!data || data.length < 3) return (
    <div style={{ padding:20, textAlign:"center", color:T.gray, fontSize:12, fontFamily:T.font }}>
      Scores needed in all 3 categories.
    </div>
  );
  const cx=size/2, cy=size/2, r=size*0.30, labelR=r+52;
  const angles=[-90,30,150];
  const pts  = angles.map(a=>({ x:cx+r*Math.cos(a*Math.PI/180), y:cy+r*Math.sin(a*Math.PI/180) }));
  const sPts = data.map((d,i)=>({ x:cx+r*(Math.max(0,d.value)/10)*Math.cos(angles[i]*Math.PI/180), y:cy+r*(Math.max(0,d.value)/10)*Math.sin(angles[i]*Math.PI/180) }));
  const grids=[0.25,0.5,0.75,1.0];
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow:"visible" }}>
      {grids.map((lv,li)=>(
        <polygon key={li}
          points={angles.map(a=>`${cx+r*lv*Math.cos(a*Math.PI/180)},${cy+r*lv*Math.sin(a*Math.PI/180)}`).join(" ")}
          fill={li===3?"rgba(3,78,123,0.04)":"none"}
          stroke={`rgba(3,78,123,${0.07+li*0.05})`} strokeWidth={li===3?1.8:0.8}/>
      ))}
      {pts.map((p,i)=><line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="rgba(3,78,123,0.10)" strokeWidth={1}/>)}
      <polygon points={sPts.map(p=>`${p.x},${p.y}`).join(" ")} fill="rgba(247,206,11,0.22)" stroke={T.yellow} strokeWidth={2.5} strokeLinejoin="round"/>
      {sPts.map((p,i)=><circle key={i} cx={p.x} cy={p.y} r={6} fill={T.yellow} stroke={T.dark} strokeWidth={2}/>)}
      {pts.map((p,i)=>{
        const lx=cx+labelR*Math.cos(angles[i]*Math.PI/180);
        const ly=cy+labelR*Math.sin(angles[i]*Math.PI/180);
        const m=FIXED_META[data[i].label]||{color:T.gray};
        return (
          <g key={i}>
            <text x={lx} y={ly-10} textAnchor="middle" fontSize={12} fontWeight="800" fill={T.blue} fontFamily={T.font} letterSpacing="0.05em">{data[i].label.toUpperCase()}</text>
            <text x={lx} y={ly+12} textAnchor="middle" fontSize={20} fontWeight="900" fill={m.color} fontFamily={T.font}>{data[i].value.toFixed(1)}</text>
          </g>
        );
      })}
    </svg>
  );
}

// ─── MiniSparkline — IDENTICAL to ParentDashboard ────────────────────────────
function MiniSparkline({ trend, catKey, color }) {
  const pts = useMemo(()=>trend.filter(t=>t[catKey]!==undefined).map(t=>t[catKey]||0),[trend,catKey]);
  if (pts.length < 2) return <div style={{ height:28 }}/>;
  const W=120, H=28, pad=2;
  const iW=W-pad*2, iH=H-pad*2;
  const min=Math.min(...pts), max=Math.max(...pts)||1, rng=max-min||1;
  const xOf=(i)=>pad+i*(iW/(pts.length-1));
  const yOf=(v)=>pad+iH-((v-min)/rng)*iH;
  let d=`M ${xOf(0).toFixed(1)} ${yOf(pts[0]).toFixed(1)}`;
  for(let i=1;i<pts.length;i++){
    const cx1=((xOf(i-1)+xOf(i))/2).toFixed(1);
    d+=` C ${cx1} ${yOf(pts[i-1]).toFixed(1)}, ${cx1} ${yOf(pts[i]).toFixed(1)}, ${xOf(i).toFixed(1)} ${yOf(pts[i]).toFixed(1)}`;
  }
  return (
    <svg width={W} height={H} style={{ overflow:"visible" }}>
      <path d={d} fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round"/>
      <circle cx={xOf(pts.length-1)} cy={yOf(pts[pts.length-1])} r={2.5} fill={color}/>
    </svg>
  );
}

// ─── TrendChart — IDENTICAL to ParentDashboard ───────────────────────────────
function TrendChart({ trend, categories, chartId="main" }) {
  if(!trend||trend.length===0) return (
    <div style={{ padding:"40px 16px", textAlign:"center", color:T.gray, fontSize:12, fontFamily:T.font, display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
      <FaChartLine size={22} style={{ opacity:0.13 }}/>
      No data in this period.
    </div>
  );

  const W=680, H=280;
  const pad={t:16, r:24, b:44, l:46};
  const iW=W-pad.l-pad.r, iH=H-pad.t-pad.b;

  const timestamps=trend.map(t=>new Date(t.date).getTime());
  const minT=Math.min(...timestamps), maxT=Math.max(...timestamps), rngT=maxT-minT||1;

  // X: add 2% padding on each side so lines never touch axes
  const xPad=iW*0.02;
  const xOf=t=>pad.l + xPad + ((new Date(t.date).getTime()-minT)/rngT)*(iW-xPad*2);
  const yOf=v=>pad.t+iH-(Math.max(0,Math.min(10,v))/10)*iH;

  const yGridVals=[0,2,4,6,8,10];

  // Pick evenly-spaced tick indices — max 7, always include first and last
  const maxTicks=7;
  const tickIdxs=[];
  if(trend.length<=maxTicks){
    for(let i=0;i<trend.length;i++) tickIdxs.push(i);
  } else {
    const step=(trend.length-1)/(maxTicks-1);
    for(let i=0;i<maxTicks;i++) tickIdxs.push(Math.round(i*step));
  }

  const fmtDate=ds=>{
    const d=new Date(ds);
    const mn=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return new Date().getFullYear()===d.getFullYear()
      ? `${mn[d.getMonth()]} ${d.getDate()}`
      : `${mn[d.getMonth()]} '${String(d.getFullYear()).slice(2)}`;
  };

  // Smooth lines — light tension cubic bezier (smoother than straight, less wobbly than full bezier)
  const smoothPath=cat=>{
    const ps=trend.map(t=>({x:xOf(t),y:yOf(t[cat]||0)}));
    if(ps.length<2) return null;
    if(ps.length===2) return `M ${ps[0].x.toFixed(1)} ${ps[0].y.toFixed(1)} L ${ps[1].x.toFixed(1)} ${ps[1].y.toFixed(1)}`;
    let d=`M ${ps[0].x.toFixed(1)} ${ps[0].y.toFixed(1)}`;
    for(let i=1;i<ps.length;i++){
      const tension=0.2;
      const p0=ps[Math.max(0,i-2)], p1=ps[i-1], p2=ps[i], p3=ps[Math.min(ps.length-1,i+1)];
      const cp1x=(p1.x+(p2.x-p0.x)*tension).toFixed(1);
      const cp1y=(p1.y+(p2.y-p0.y)*tension).toFixed(1);
      const cp2x=(p2.x-(p3.x-p1.x)*tension).toFixed(1);
      const cp2y=(p2.y-(p3.y-p1.y)*tension).toFixed(1);
      d+=` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
    }
    return d;
  };

  const showDots=trend.length<=30;
  const legW=iW/Math.max(categories.length,1);

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display:"block", fontFamily:T.font }}>
      {/* Plot area background — no border box */}
      <rect x={pad.l} y={pad.t} width={iW} height={iH} fill="#fafcff"/>

      {/* Horizontal grid + Y labels */}
      {yGridVals.map(v=>(
        <g key={v}>
          <line x1={pad.l} x2={pad.l+iW} y1={yOf(v)} y2={yOf(v)}
            stroke={v===0?"#b0bcc8":"#dde4ee"} strokeWidth={v===0?1:0.8}/>
          <text x={pad.l-8} y={yOf(v)+4} textAnchor="end" fontSize={10} fill="#7a8899" fontFamily={T.font}>{v}</text>
        </g>
      ))}

      {/* L-shaped axes only — left spine + bottom spine, no top/right border */}
      <line x1={pad.l} x2={pad.l} y1={pad.t} y2={pad.t+iH} stroke="#aab4c0" strokeWidth={1.2}/>
      <line x1={pad.l} x2={pad.l+iW} y1={pad.t+iH} y2={pad.t+iH} stroke="#aab4c0" strokeWidth={1.2}/>

      {/* X-axis ticks + labels — clipped so they never exceed plot bounds */}
      {tickIdxs.map((ti,i)=>{
        const x=xOf(trend[ti]);
        const clampedX=Math.max(pad.l+20, Math.min(pad.l+iW-20, x));
        return (
          <g key={i}>
            <line x1={x} x2={x} y1={pad.t+iH} y2={pad.t+iH+4} stroke="#aab4c0" strokeWidth={0.9}/>
            <text x={clampedX} y={pad.t+iH+12} textAnchor="middle" fontSize={10} fill="#7a8899" fontFamily={T.font}>
              {fmtDate(trend[ti].date)}
            </text>
          </g>
        );
      })}

      {/* Area fills (subtle) */}
      {categories.map(cat=>{
        const m=FIXED_META[cat]||getCatMeta(cat,0);
        const ps=trend.map(t=>({x:xOf(t),y:yOf(t[cat]||0)}));
        if(ps.length<2) return null;
        const sp=smoothPath(cat);
        const base=pad.t+iH;
        const areaD=`${sp} L ${ps[ps.length-1].x.toFixed(1)} ${base} L ${ps[0].x.toFixed(1)} ${base} Z`;
        return (
          <g key={`area-${cat}`}>
            <defs>
              <linearGradient id={`${chartId}-ag-${cat}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={m.color} stopOpacity="0.12"/>
                <stop offset="100%" stopColor={m.color} stopOpacity="0.01"/>
              </linearGradient>
            </defs>
            <path d={areaD} fill={`url(#${chartId}-ag-${cat})`} clipPath={`url(#${chartId}-clip)`}/>
          </g>
        );
      })}

      {/* Clip so lines/areas don't spill outside plot box */}
      <defs>
        <clipPath id={`${chartId}-clip`}>
          <rect x={pad.l} y={pad.t} width={iW} height={iH}/>
        </clipPath>
      </defs>

      {/* Lines — clipped */}
      <g clipPath={`url(#${chartId}-clip)`}>
        {categories.map(cat=>{
          const m=FIXED_META[cat]||getCatMeta(cat,0);
          const sp=smoothPath(cat);
          return sp ? <path key={`l-${cat}`} d={sp} fill="none" stroke={m.color} strokeWidth={2.2} strokeLinejoin="round" strokeLinecap="round"/> : null;
        })}
      </g>

      {/* Dots — only when few points */}
      {showDots && (
        <g clipPath={`url(#${chartId}-clip)`}>
          {categories.map(cat=>{
            const m=FIXED_META[cat]||getCatMeta(cat,0);
            return trend.map((t,i)=>t[cat]!==undefined ? (
              <circle key={`d-${cat}-${i}`} cx={xOf(t)} cy={yOf(t[cat]||0)} r={3.5} fill="white" stroke={m.color} strokeWidth={1.8}/>
            ) : null);
          })}
        </g>
      )}

      {/* Legend row — centred below plot */}
      {categories.map((cat,i)=>{
        const m=FIXED_META[cat]||getCatMeta(cat,i);
        const lx=pad.l + legW*i + legW/2 - 34;
        return (
          <g key={`leg-${cat}`} transform={`translate(${lx},${H-9})`}>
            <line x1={0} x2={20} y1={0} y2={0} stroke={m.color} strokeWidth={2.5} strokeLinecap="round"/>
            <circle cx={10} cy={0} r={3.5} fill="white" stroke={m.color} strokeWidth={1.8}/>
            <text x={25} y={4} fontSize={11} fill={T.dark} fontFamily={T.font} fontWeight={700}>{cat}</text>
          </g>
        );
      })}
    </svg>
  );
}

// ─── CategoryBarChart — premium bar chart, bars always inside axes ─────────
function CategoryBarChart({ trend, category, color, chartId }) {
  if(!trend||trend.length===0) return <div style={{ height:160, display:"flex", alignItems:"center", justifyContent:"center", color:T.gray, fontSize:13, fontFamily:T.font }}>No data</div>;
  const sessions=trend.map(t=>({date:t.date,value:t[category]||0})).filter(s=>s.value>0);
  if(!sessions.length) return <div style={{ height:160, display:"flex", alignItems:"center", justifyContent:"center", color:T.gray, fontSize:13, fontFamily:T.font }}>No data</div>;

  const W=300, H=160;
  // Generous padding: left for y-labels, bottom for x-labels, top/right breathing room
  const pad={t:10, r:10, b:26, l:34};
  const iW=W-pad.l-pad.r, iH=H-pad.t-pad.b;

  const n=sessions.length;
  // Use ordinal x — bar centres are evenly distributed within the plot area
  // This guarantees no bar ever touches or crosses the axes
  const slotW=iW/n;
  const barW=Math.max(4, Math.min(18, slotW*0.6));
  const xOf=i=>pad.l + slotW*i + slotW/2;  // ordinal: evenly spaced
  const yOf=v=>pad.t+iH-(Math.max(0,Math.min(10,v))/10)*iH;

  const fmtDate=ds=>{
    const d=new Date(ds);
    const mn=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${mn[d.getMonth()]} ${d.getDate()}`;
  };

  // Pick tick positions that won't overlap: first, last, and ~2 middle ones
  const maxXLabels=4;
  const tickIdxs=[];
  if(n<=maxXLabels){
    for(let i=0;i<n;i++) tickIdxs.push(i);
  } else {
    const step=(n-1)/(maxXLabels-1);
    for(let i=0;i<maxXLabels;i++) tickIdxs.push(Math.round(i*step));
  }

  const yGridVals=[0,5,10];

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display:"block", fontFamily:T.font }}>
      <defs>
        <linearGradient id={`${chartId}-bg`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.88"/>
          <stop offset="100%" stopColor={color} stopOpacity="0.25"/>
        </linearGradient>
        <clipPath id={`${chartId}-clip`}>
          <rect x={pad.l} y={pad.t} width={iW} height={iH}/>
        </clipPath>
      </defs>

      {/* Plot area background — no border box */}
      <rect x={pad.l} y={pad.t} width={iW} height={iH} fill="#fafcff"/>

      {/* Y grid + labels */}
      {yGridVals.map(v=>(
        <g key={v}>
          <line x1={pad.l} x2={pad.l+iW} y1={yOf(v)} y2={yOf(v)}
            stroke={v===0?"#b0bcc8":"#dde4ee"} strokeWidth={v===0?1:0.7}/>
          <text x={pad.l-5} y={yOf(v)+4} textAnchor="end" fontSize={9} fill="#7a8899" fontFamily={T.font}>{v}</text>
        </g>
      ))}

      {/* L-shaped axes only — no top/right border */}
      <line x1={pad.l} x2={pad.l} y1={pad.t} y2={pad.t+iH} stroke="#aab4c0" strokeWidth={0.9}/>
      <line x1={pad.l} x2={pad.l+iW} y1={pad.t+iH} y2={pad.t+iH} stroke="#aab4c0" strokeWidth={0.9}/>

      {/* Bars — clipped so they never escape the plot box */}
      <g clipPath={`url(#${chartId}-clip)`}>
        {sessions.map((s,i)=>{
          const x=xOf(i), y=yOf(s.value), h=pad.t+iH-y;
          return (
            <g key={i}>
              <rect x={x-barW/2} y={y} width={barW} height={h} rx={2} fill={`url(#${chartId}-bg)`}/>
              <rect x={x-barW/2} y={y} width={barW} height={Math.min(2.5,h)} rx={1} fill={color}/>
            </g>
          );
        })}
      </g>

      {/* X-axis tick labels — only picked ticks, no overlap */}
      {tickIdxs.map((ti,i)=>{
        const x=xOf(ti);
        return (
          <g key={i}>
            <line x1={x} x2={x} y1={pad.t+iH} y2={pad.t+iH+3} stroke="#aab4c0" strokeWidth={0.7}/>
            <text x={x} y={H-3} textAnchor="middle" fontSize={9} fill="#7a8899" fontFamily={T.font}>{fmtDate(sessions[ti].date)}</text>
          </g>
        );
      })}
    </svg>
  );
}

// ─── Student search bar ───────────────────────────────────────────────────────
function StudentSearchBar({ onSelect }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const [searchText,    setSearchText]    = useState("");
  const [searchResults, setSearchResults] = useState([]);

  useEffect(()=>{
    if (searchText.trim().length < 2) { setSearchResults([]); return; }
    const t = setTimeout(()=>{
      fetch(`${BASE}/students/search?name=${encodeURIComponent(searchText)}`)
        .then(r=>r.json())
        .then(d=>setSearchResults(Array.isArray(d.students)?d.students:[]))
        .catch(()=>setSearchResults([]));
    }, 300);
    return ()=>clearTimeout(t);
  }, [searchText]);

  return (
    <div style={{ position:"relative", width:300 }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 13px", borderRadius:10, background:T.inputBg, border:`1.5px solid ${T.inputBdr}` }}>
        <FaSearch size={12} style={{ color:T.textMuted, flexShrink:0 }}/>
        <input
          value={searchText}
          onChange={e=>setSearchText(e.target.value)}
          placeholder="Search student by name…"
          style={{ border:"none", outline:"none", background:"transparent", fontSize:13, color:T.inputText, fontFamily:T.font, width:"100%" }}
        />
      </div>
      {searchResults.length > 0 && (
        <div style={{ position:"absolute", top:"calc(100% + 6px)", left:0, width:"100%", background:T.dropdownBg, backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)", border:T.dropdownBdr, borderRadius:12, boxShadow:"0 8px 24px rgba(0,0,0,0.20)", zIndex:30, maxHeight:200, overflowY:"auto", WebkitOverflowScrolling:"touch" }}>
          {searchResults.map((s,i)=>(
            <div key={s.student_id}
              onClick={()=>{ setSearchResults([]); setSearchText(""); onSelect(s); }}
              style={{ padding:"10px 14px", cursor:"pointer", WebkitTapHighlightColor:"transparent", fontSize:13, borderBottom:i<searchResults.length-1?"1px solid rgba(0,53,128,0.07)":"none", color:"#1a2840", fontWeight:500, display:"flex", alignItems:"center", gap:8 }}
              onMouseEnter={e=>e.currentTarget.style.background="rgba(247,206,11,0.12)"}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <FaUser size={10} style={{ color:T.gray }}/>
              {s.first_name} {s.last_name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Stat block (same as DashboardTab in ParentDashboard) ─────────────────────
function statBlock(label, value, color) {
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", minWidth:52, padding:"0 4px" }}>
      <div style={{ fontSize:9, fontWeight:700, color:T.gray, textTransform:"uppercase", letterSpacing:"0.09em", fontFamily:T.font, marginBottom:2 }}>{label}</div>
      <div style={{ fontSize:22, fontWeight:900, color:color||T.blue, lineHeight:1, fontFamily:T.font }}>{value}</div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PERFORMANCE REPORT TAB (mode="dashboard")
// Mirrors ParentDashboard DashboardTab exactly
// ══════════════════════════════════════════════════════════════════════════════
function PerformanceReportTab({ dashboard }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const gc = glassCard(T);
  const globalTf   = useTimeframe();
  const battingTf  = useTimeframe();
  const bowlingTf  = useTimeframe();
  const fieldingTf = useTimeframe();

  const trend_raw       = dashboard?.trend           || [];
  const category_totals = dashboard?.category_totals || [];
  const comments        = dashboard?.comments        || [];
  const student         = dashboard?.student         || {};

  const filteredTrend    = useMemo(()=>filterByTimeframe(trend_raw,globalTf.tfId,globalTf.customFrom,globalTf.customTo),[trend_raw,globalTf.tfId,globalTf.customFrom,globalTf.customTo]);
  const battingFiltered  = useMemo(()=>filterByTimeframe(trend_raw,battingTf.tfId,battingTf.customFrom,battingTf.customTo),[trend_raw,battingTf.tfId,battingTf.customFrom,battingTf.customTo]);
  const bowlingFiltered  = useMemo(()=>filterByTimeframe(trend_raw,bowlingTf.tfId,bowlingTf.customFrom,bowlingTf.customTo),[trend_raw,bowlingTf.tfId,bowlingTf.customFrom,bowlingTf.customTo]);
  const fieldingFiltered = useMemo(()=>filterByTimeframe(trend_raw,fieldingTf.tfId,fieldingTf.customFrom,fieldingTf.customTo),[trend_raw,fieldingTf.tfId,fieldingTf.customFrom,fieldingTf.customTo]);

  const radarData = useMemo(()=>["Batting","Bowling","Fielding"].map(cat=>{
    const rows=filteredTrend.filter(t=>t[cat]!==undefined);
    const avg=rows.length?rows.reduce((s,t)=>s+(t[cat]||0),0)/rows.length:0;
    return {label:cat,value:avg};
  }),[filteredTrend]);

  const totalKPIs  = category_totals.reduce((s,c)=>s+(c.count||0),0);
  const overallAvg = totalKPIs>0?(category_totals.reduce((s,c)=>s+(parseFloat(c.total)||0),0)/totalKPIs).toFixed(1):"—";
  const age        = student?.date_of_birth?Math.floor((new Date()-new Date(student.date_of_birth))/(365.25*864e5)):null;
  const cats       = ["Batting","Bowling","Fielding"].filter(c=>category_totals.find(ct=>ct.category===c));

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:10 }}>

      {/* ── Student hero bar — identical to DashboardTab ── */}
      <div style={{ display:"flex", alignItems:"stretch", borderRadius:12, boxShadow:"0 3px 18px rgba(0,40,100,0.11)", overflow:"hidden", border:"1px solid rgba(0,53,128,0.10)", background:"#ffffff", minHeight:80 }}>

        <div style={{ display:"flex", alignItems:"center", gap:14, padding:"12px 22px", flex:"0 0 auto", minWidth:260, background:"linear-gradient(118deg,#02304f 0%,#034e7b 55%,#0369a1 100%)", position:"relative", overflow:"hidden" }}>
          <div style={{ position:"absolute",right:-16,top:-16,width:90,height:90,borderRadius:"50%",background:"rgba(247,206,11,0.06)",pointerEvents:"none" }}/>
          <div style={{ width:42,height:42,borderRadius:"50%",flexShrink:0,background:"rgba(255,255,255,0.12)",border:`2px solid ${T.yellow}55`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:900,color:"#ffffff",overflow:"hidden",boxShadow:"0 2px 8px rgba(0,0,0,0.20)",zIndex:1 }}>
            {student?.photo_url
              ?<img src={`${BASE}/${student.photo_url}`} alt="" style={{ width:"100%",height:"100%",objectFit:"cover" }}/>
              :`${student?.first_name?.[0]||""}${student?.last_name?.[0]||""}`}
          </div>
          <div style={{ zIndex:1,minWidth:0 }}>
            <div style={{ fontSize:15,fontWeight:900,color:"#ffffff",letterSpacing:"-0.01em",fontFamily:T.font,lineHeight:1.15,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>
              {student?.first_name} {student?.last_name}
            </div>
            <div style={{ display:"flex",gap:14,marginTop:4,alignItems:"center",flexWrap:"nowrap" }}>
              {[
                {icon:FaCalendarAlt, label:"Joined", val:student?.join_date?.slice(0,10)||"—"},
                {icon:FaVenusMars,   label:"Age",    val:age?`${age} yrs`:"—"},
                {icon:FaIdCard,      label:"Gender", val:student?.gender||"—"},
                {icon:FaMedal,       label:"KPIs",   val:totalKPIs},
              ].map(item=>(
                <div key={item.label} style={{ display:"flex",alignItems:"center",gap:4,flexShrink:0 }}>
                  <item.icon size={8} style={{ color:"rgba(255,255,255,0.65)" }}/>
                  <div>
                    <div style={{ fontSize:8,fontWeight:700,color:"rgba(255,255,255,0.60)",textTransform:"uppercase",letterSpacing:"0.07em",fontFamily:T.font,lineHeight:1 }}>{item.label}</div>
                    <div style={{ fontSize:11,fontWeight:700,color:"#ffffff",fontFamily:T.font,lineHeight:1.2 }}>{item.val}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ width:1,background:"#e2e8f0",flexShrink:0 }}/>

        <div style={{ display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"10px 22px",flexShrink:0,gap:1,background:"#ffffff" }}>
          <FaFire size={11} style={{ color:T.yellow }}/>
          <div style={{ fontSize:8,fontWeight:800,color:T.gray,textTransform:"uppercase",letterSpacing:"0.09em",fontFamily:T.font,marginTop:1 }}>Total Rating</div>
          <div style={{ fontSize:34,fontWeight:900,color:T.yellow,lineHeight:1,fontFamily:T.font }}>{overallAvg}</div>
          <div style={{ fontSize:9,color:T.gray,fontFamily:T.font }}>/10 avg</div>
        </div>

        <div style={{ width:1,background:"#e2e8f0",flexShrink:0 }}/>

        {["Batting","Bowling","Fielding"].map((cat,ci)=>{
          const m  = FIXED_META[cat];
          const ct = category_totals.find(c=>c.category===cat);
          const avg= ct?parseFloat(ct.avg).toFixed(1):"—";
          const tot= ct?parseFloat(ct.total).toFixed(0):"—";
          const pct= ct?Math.round((parseFloat(ct.avg)/10)*100):0;
          return (
            <React.Fragment key={cat}>
              <div style={{ display:"flex",alignItems:"center",gap:13,padding:"10px 20px",flex:1,background:"#ffffff",minWidth:0 }}>
                <div style={{ width:40,height:40,borderRadius:10,background:m.lightBg,border:`1.5px solid ${m.border}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                  <m.icon size={17} style={{ color:m.color }}/>
                </div>
                <div style={{ flex:1,minWidth:0 }}>
                  <div style={{ fontSize:9,fontWeight:800,color:T.gray,textTransform:"uppercase",letterSpacing:"0.08em",fontFamily:T.font,marginBottom:1 }}>{cat}</div>
                  <div style={{ display:"flex",alignItems:"baseline",gap:3,marginBottom:3 }}>
                    <span style={{ fontSize:26,fontWeight:900,color:m.color,lineHeight:1,fontFamily:T.font }}>{avg}</span>
                    <span style={{ fontSize:11,color:T.gray,fontFamily:T.font }}>/10</span>
                  </div>
                  <div style={{ height:3,borderRadius:2,background:"rgba(0,53,128,0.07)",overflow:"hidden",marginBottom:5 }}>
                    <div style={{ height:"100%",width:`${pct}%`,borderRadius:2,background:m.grad,transition:"width 0.6s" }}/>
                  </div>
                  <div style={{ display:"flex",gap:14 }}>
                    <div>
                      <div style={{ fontSize:8,fontWeight:800,color:T.gray,textTransform:"uppercase",letterSpacing:"0.07em",fontFamily:T.font }}>Total Pts</div>
                      <div style={{ fontSize:13,fontWeight:900,color:T.blue,fontFamily:T.font,lineHeight:1.2 }}>{tot}</div>
                    </div>
                    <div>
                      <div style={{ fontSize:8,fontWeight:800,color:T.gray,textTransform:"uppercase",letterSpacing:"0.07em",fontFamily:T.font }}>KPIs</div>
                      <div style={{ fontSize:13,fontWeight:900,color:T.blue,fontFamily:T.font,lineHeight:1.2 }}>{ct?.count||"—"}</div>
                    </div>
                    <div>
                      <div style={{ fontSize:8,fontWeight:800,color:T.gray,textTransform:"uppercase",letterSpacing:"0.07em",fontFamily:T.font }}>Progress</div>
                      <div style={{ fontSize:13,fontWeight:900,color:m.color,fontFamily:T.font,lineHeight:1.2 }}>{pct}%</div>
                    </div>
                  </div>
                </div>
              </div>
              {ci < 2 && <div style={{ width:1,background:"#e2e8f0",flexShrink:0 }}/>}
            </React.Fragment>
          );
        })}
      </div>

      {/* ── Mini category bar charts ── */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10 }}>
        {[
          {cat:"Batting", tf:battingTf,  filtered:battingFiltered,  id:"cdr-bat"},
          {cat:"Bowling", tf:bowlingTf,  filtered:bowlingFiltered,  id:"cdr-bow"},
          {cat:"Fielding",tf:fieldingTf, filtered:fieldingFiltered, id:"cdr-fld"},
        ].map(({cat,tf,filtered,id})=>{
          const m=FIXED_META[cat];
          const vals=filtered.map(t=>t[cat]).filter(v=>v!==undefined);
          const avg=vals.length?(vals.reduce((a,b)=>a+b,0)/vals.length).toFixed(1):"—";
          const peak=vals.length?Math.max(...vals).toFixed(1):"—";
          const last=vals.length?vals[vals.length-1].toFixed(1):"—";
          return (
            <div key={cat} style={{ ...gc, padding:"14px 16px 12px", borderTop:`3px solid ${m.color}` }}>
              <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10 }}>
                <div style={{ display:"flex",alignItems:"center",gap:7 }}>
                  <div style={{ width:28,height:28,borderRadius:8,background:m.lightBg,border:`1.5px solid ${m.border}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                    <m.icon size={13} style={{ color:m.color }}/>
                  </div>
                  <span style={{ fontSize:14,fontWeight:800,color:T.blue,fontFamily:T.font }}>{cat}</span>
                </div>
                <TfPill tf={tf}/>
              </div>
              <CategoryBarChart trend={filtered} category={cat} color={m.color} chartId={id}/>
              <div style={{ display:"flex",gap:0,marginTop:10,borderTop:"1px solid rgba(0,53,128,0.07)",paddingTop:10,justifyContent:"space-around" }}>
                {statBlock("Avg",      avg,        m.color)}
                {statBlock("Peak",     peak,       m.color)}
                {statBlock("Latest",   last,       m.color)}
                {vals.length>0 && statBlock("Sessions", vals.length, T.blue)}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Performance over time ── */}
      <div style={{ ...gc, padding:"16px 20px 14px" }}>
        <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,gap:8 }}>
          <span style={{ fontSize:13,fontWeight:800,letterSpacing:"0.07em",textTransform:"uppercase",color:T.gray,fontFamily:T.font,whiteSpace:"nowrap" }}>Performance Over Time</span>
          <TfPill tf={globalTf}/>
        </div>
        <TrendChart trend={filteredTrend} categories={cats} chartId="cdr-combined"/>
        {filteredTrend.length>0&&(
          <div style={{ display:"flex",gap:12,marginTop:5,justifyContent:"flex-end",flexWrap:"wrap" }}>
            {cats.map(cat=>{
              const vals=filteredTrend.map(t=>t[cat]).filter(v=>v!==undefined);
              if(!vals.length) return null;
              const avg=(vals.reduce((a,b)=>a+b,0)/vals.length).toFixed(1);
              const last=vals[vals.length-1]?.toFixed(1);
              const m=FIXED_META[cat]||{color:T.gray};
              return (
                <div key={cat} style={{ fontSize:12,fontFamily:T.font,color:T.gray,display:"flex",alignItems:"center",gap:4 }}>
                  <div style={{ width:10,height:2.5,borderRadius:2,background:m.color }}/>
                  <span style={{ fontWeight:700,color:m.color }}>{cat}</span>
                  <span>avg <strong style={{ color:T.blue }}>{avg}</strong></span>
                  <span>· latest <strong style={{ color:T.blue }}>{last}</strong></span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── KPI Triangle ── */}
      <div style={{ ...gc, padding:"14px 16px 16px" }}>
        <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8 }}>
          <span style={{ fontSize:13,fontWeight:800,letterSpacing:"0.07em",textTransform:"uppercase",color:T.gray,fontFamily:T.font }}>KPI Triangle</span>
          <TfPill tf={globalTf}/>
        </div>
        <div style={{ display:"flex",alignItems:"center",justifyContent:"center",padding:"8px 0 4px" }}>
          <TriangleRadar data={radarData} size={280}/>
        </div>
      </div>

      {/* ── Coach comments ── */}
      <div style={{ ...gc, padding:"10px 14px 12px" }}>
        <SectionLabel>Coach Comments</SectionLabel>
        {comments.length>0?(
          <div style={{ display:"flex",flexDirection:"column",gap:7 }}>
            {comments.map((c,i)=>(
              <div key={i} style={{ padding:"8px 12px",borderRadius:9,background:i%2===0?T.rowEven:T.rowOdd,border:T.glassBdr }}>
                <div style={{ display:"flex",alignItems:"center",gap:6,marginBottom:4 }}>
                  <div style={{ width:18,height:18,borderRadius:"50%",background:`linear-gradient(135deg,${T.yellow},#fff3a0)`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                    <FaCommentDots size={7} color={T.blue}/>
                  </div>
                  <span style={{ fontSize:9,fontWeight:700,color:T.gray,fontFamily:T.font,letterSpacing:"0.06em",textTransform:"uppercase" }}>{c.date}</span>
                </div>
                <p style={{ margin:0,fontSize:12,color:"#1a2840",lineHeight:1.6,fontFamily:T.font }}>{c.text}</p>
              </div>
            ))}
          </div>
        ):(
          <div style={{ color:T.gray,fontSize:12,fontFamily:T.font,padding:"6px 0" }}>No coach comments recorded yet.</div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PROGRESS SUMMARY TAB (mode="metrics")
// Mirrors ParentDashboard MetricsTab exactly
// ══════════════════════════════════════════════════════════════════════════════
function ProgressSummaryTab({ dashboard }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const gc = glassCard(T);
  if (!dashboard) return null;
  const category_totals = dashboard.category_totals || [];
  const latest_scores   = dashboard.latest_scores   || [];
  const trend_raw       = dashboard.trend            || [];

  const totalKPIs  = category_totals.reduce((s,c)=>s+(c.count||0),0);
  const overallAvg = totalKPIs>0?(category_totals.reduce((s,c)=>s+(parseFloat(c.total)||0),0)/totalKPIs).toFixed(1):"—";
  const totalPts   = category_totals.reduce((s,c)=>s+(parseFloat(c.total)||0),0).toFixed(0);

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

      {/* ── Summary banner ── */}
      <div style={{ borderRadius:14, padding:"12px 18px", background:"linear-gradient(135deg,#02304f 0%,#034e7b 45%,#045a8d 75%,#0369a1 100%)", boxShadow:"0 6px 28px rgba(2,48,79,0.26), 0 1px 4px rgba(0,40,100,0.10)", border:"1px solid rgba(255,255,255,0.10)", display:"flex", alignItems:"center", gap:18, flexWrap:"wrap" }}>
        <div style={{ width:90,height:90,borderRadius:14,flexShrink:0,background:"rgba(247,206,11,0.16)",border:`1.5px solid ${T.yellow}44`,backdropFilter:"blur(12px)", WebkitBackdropFilter:"blur(12px)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:1 }}>
          <FaFire size={11} style={{ color:T.yellow }}/>
          <div style={{ fontSize:7.5,fontWeight:800,color:"rgba(255,255,255,0.42)",textTransform:"uppercase",letterSpacing:"0.08em",fontFamily:T.font }}>Rating</div>
          <div style={{ fontSize:38,fontWeight:900,color:T.yellow,lineHeight:1,fontFamily:T.font }}>{overallAvg}</div>
          <div style={{ fontSize:8,color:"rgba(255,255,255,0.28)",fontFamily:T.font }}>/10 avg</div>
        </div>
        <div style={{ flex:1, minWidth:200 }}>
          <div style={{ fontSize:15,fontWeight:900,color:"white",fontFamily:T.font,marginBottom:6 }}>Overall Performance Summary</div>
          <div style={{ display:"flex",gap:8,flexWrap:"wrap" }}>
            {category_totals.map((ct,idx)=>{
              const m=getCatMeta(ct.category,idx);
              return (
                <div key={ct.category} style={{ display:"flex",alignItems:"center",gap:6,padding:"5px 13px",borderRadius:20,background:"rgba(255,255,255,0.09)",border:`1px solid ${m.color}38` }}>
                  <m.icon size={11} style={{ color:m.color }}/>
                  <span style={{ fontSize:13,color:"rgba(255,255,255,0.70)",fontFamily:T.font }}>{ct.category}</span>
                  <span style={{ fontSize:16,fontWeight:900,color:m.color,fontFamily:T.font }}>{parseFloat(ct.avg).toFixed(1)}</span>
                </div>
              );
            })}
          </div>
          <div style={{ fontSize:11,color:"rgba(255,255,255,0.40)",fontFamily:T.font,marginTop:8 }}>
            {totalKPIs} KPIs · {category_totals.length} categories · {totalPts} total pts
          </div>
        </div>
      </div>

      {/* ── Category cards ── */}
      <div>
        <SectionLabel>Performance Categories</SectionLabel>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:12 }}>
          {category_totals.map((ct,idx)=>{
            const m    = getCatMeta(ct.category,idx);
            const Icon = m.icon;
            const avg  = parseFloat(ct.avg)||0;
            const pct  = Math.round((avg/10)*100);
            const tot  = parseFloat(ct.total)||0;
            return (
              <div key={ct.category} style={{ background:"#ffffff", border:`1.5px solid ${m.border}`, borderRadius:14, boxShadow:`0 4px 18px ${m.color}14, 0 1px 4px rgba(0,40,100,0.05)`, overflow:"hidden", position:"relative", transition:"transform 0.16s, box-shadow 0.16s" }}
                onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow=`0 10px 28px ${m.color}28, 0 2px 8px rgba(0,40,100,0.08)`;}}
                onMouseLeave={e=>{e.currentTarget.style.transform="";e.currentTarget.style.boxShadow=`0 4px 18px ${m.color}14, 0 1px 4px rgba(0,40,100,0.05)`;}}
              >
                <div style={{ height:3, background:m.grad, width:"100%" }}/>
                <div style={{ padding:"12px 14px 14px" }}>
                  <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10 }}>
                    <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                      <div style={{ width:34,height:34,borderRadius:9,background:m.lightBg,border:`1px solid ${m.border}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                        <Icon size={15} style={{ color:m.color }}/>
                      </div>
                      <div>
                        <div style={{ fontSize:14,fontWeight:800,color:T.blue,fontFamily:T.font }}>{ct.category}</div>
                        <div style={{ fontSize:11,color:T.gray,fontFamily:T.font }}>{ct.count} KPIs</div>
                      </div>
                    </div>
                    <div style={{ padding:"4px 11px",borderRadius:16,background:m.lightBg,border:`1px solid ${m.border}` }}>
                      <span style={{ fontSize:22,fontWeight:900,color:m.color,fontFamily:T.font,lineHeight:1 }}>{avg.toFixed(1)}</span>
                      <span style={{ fontSize:11,color:T.gray,fontFamily:T.font }}>/10</span>
                    </div>
                  </div>
                  <div style={{ height:4,borderRadius:2,background:"rgba(0,53,128,0.07)",overflow:"hidden",marginBottom:10 }}>
                    <div style={{ height:"100%",width:`${pct}%`,borderRadius:2,background:m.grad,transition:"width 0.6s ease" }}/>
                  </div>
                  <div style={{ marginBottom:8 }}>
                    <MiniSparkline trend={trend_raw} catKey={ct.category} color={m.color}/>
                  </div>
                  <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:6 }}>
                    {[{label:"Total Pts",val:tot.toFixed(0)},{label:"Progress",val:`${pct}%`}].map(stat=>(
                      <div key={stat.label} style={{ padding:"6px 8px",borderRadius:8,background:"#ffffff",border:`1px solid ${m.border}` }}>
                        <div style={{ fontSize:10,fontWeight:700,color:T.gray,textTransform:"uppercase",letterSpacing:"0.06em",fontFamily:T.font }}>{stat.label}</div>
                        <div style={{ fontSize:16,fontWeight:900,color:m.color,fontFamily:T.font,lineHeight:1.2 }}>{stat.val}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Per-indicator breakdowns ── */}
      {category_totals.map((ct,idx)=>{
        const inds=latest_scores.filter(s=>s.category_name===ct.category);
        if(!inds.length) return null;
        const m=getCatMeta(ct.category,idx);
        return (
          <div key={ct.category}>
            <div style={{ display:"flex",alignItems:"center",gap:10,padding:"9px 16px",borderRadius:10,marginBottom:10,background:`linear-gradient(110deg,${T.dark},rgba(3,62,98,0.96))`,border:"1px solid rgba(255,255,255,0.12)",boxShadow:"0 4px 16px rgba(3,78,123,0.18)",position:"relative",overflow:"hidden" }}>
              <div style={{ position:"absolute",left:0,top:0,bottom:0,width:3,background:`linear-gradient(180deg,${m.color},${m.color}66)`,borderRadius:"10px 0 0 10px" }}/>
              <div style={{ width:26,height:26,borderRadius:7,marginLeft:3,flexShrink:0,background:`linear-gradient(135deg,${T.yellow},#fff3a0)`,display:"flex",alignItems:"center",justifyContent:"center" }}>
                <m.icon size={11} color={T.blue}/>
              </div>
              <span style={{ fontWeight:800,color:"white",fontSize:13,flex:1,fontFamily:T.font }}>{ct.category}</span>
              <span style={{ padding:"2px 8px",borderRadius:14,background:"rgba(255,255,255,0.11)",border:"1px solid rgba(255,255,255,0.16)",fontSize:9,fontWeight:700,color:"rgba(255,255,255,0.70)",fontFamily:T.font }}>{inds.length} KPIs</span>
            </div>
            <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:8 }}>
              {inds.map(ind=>{
                const sc=ind.score||0;
                const bg=sc>=8?"rgba(16,185,129,0.08)":sc>=5?"rgba(245,158,11,0.08)":"rgba(220,0,0,0.05)";
                const bdr=sc>=8?"rgba(16,185,129,0.20)":sc>=5?"rgba(245,158,11,0.22)":"rgba(220,0,0,0.14)";
                const col=sc>=8?"#10b981":sc>=5?"#d97706":"#c0392b";
                return (
                  <div key={ind.indicator_id} style={{ background:"#fff",border:`1.5px solid ${bdr}`,borderRadius:10,padding:"12px 14px",display:"flex",flexDirection:"column",gap:7 }}>
                    <div style={{ fontSize:13,fontWeight:700,color:T.blue,fontFamily:T.font,lineHeight:1.3 }}>{ind.indicator_name}</div>
                    <div style={{ height:4,borderRadius:2,background:"rgba(0,53,128,0.08)",overflow:"hidden" }}>
                      <div style={{ height:"100%",borderRadius:2,width:`${sc*10}%`,background:`linear-gradient(90deg,${col},${col}99)`,transition:"width 0.6s ease" }}/>
                    </div>
                    <div style={{ display:"flex",alignItems:"baseline",gap:3 }}>
                      <span style={{ fontSize:22,fontWeight:900,color:col,fontFamily:T.font,lineHeight:1 }}>{sc}</span>
                      <span style={{ fontSize:11,color:T.gray }}>/10</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ROOT — CoachPerformanceView
// mode: "dashboard" → Performance Report  (mirrors DashboardTab)
// mode: "metrics"   → Progress Summary    (mirrors MetricsTab)
// ══════════════════════════════════════════════════════════════════════════════
export default function CoachPerformanceView({ mode = "dashboard", currentUser }) {
  const { config } = useAppConfig();
  const { isDark } = useTheme();
  const T = getT(isDark);
  const gc = glassCard(T);

  const fontFam    = `'${config.font_family||"Segoe UI"}', system-ui, sans-serif`;

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [dashboard,       setDashboard]       = useState(null);
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState(null);

  const title = mode === "dashboard" ? "Performance Report" : "Progress Summary";
  const Icon  = mode === "dashboard" ? FaChartLine : FaTrophy;

  const loadDashboard = async (student) => {
    if (!student) { setDashboard(null); return; }
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${BASE}/performance/dashboard/${student.student_id}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      if (d.error) throw new Error(d.error);
      setDashboard(d);
    } catch (e) {
      setError(e.message || "Failed to load dashboard.");
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (student) => {
    setSelectedStudent(student);
    loadDashboard(student);
  };

  return (
    <div style={{ padding:"20px 24px 50px", fontFamily:fontFam, minHeight:"100%", background:T.pageBg }}>

      {/* ── Page header ── */}
      <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:18 }}>
        <div style={{ width:40,height:40,borderRadius:12,background:`linear-gradient(135deg,${T.yellow},#fff3a0)`,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 3px 12px rgba(255,200,0,0.28)",flexShrink:0 }}>
          <Icon size={16} color={T.blue}/>
        </div>
        <div>
          <h2 style={{ margin:0,fontSize:20,fontWeight:800,color:T.blue,fontFamily:fontFam,letterSpacing:"-0.01em" }}>{title}</h2>
          <p style={{ margin:"2px 0 0",fontSize:12,color:T.gray,fontFamily:fontFam }}>
            {mode==="dashboard"?"Full performance dashboard with trendlines and KPI radar":"Metrics & KPI breakdown across all performance categories"}
          </p>
        </div>
      </div>

      {/* ── Student selector ── */}
      <div style={{ ...gc, padding:"14px 18px", marginBottom:18 }}>
        <div style={{ fontSize:11,fontWeight:800,color:T.blue,letterSpacing:"0.07em",textTransform:"uppercase",fontFamily:fontFam,marginBottom:10 }}>Select Student</div>
        <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
          {!selectedStudent
            ? <StudentSearchBar onSelect={handleSelect}/>
            : (
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ display:"flex",alignItems:"center",gap:8,padding:"8px 14px",borderRadius:10,background:`${T.yellow}22`,border:`1px solid ${T.yellow}44` }}>
                  <FaUser size={11} style={{ color:T.blue }}/>
                  <span style={{ fontSize:13,fontWeight:700,color:T.blue,fontFamily:fontFam }}>
                    {selectedStudent.first_name} {selectedStudent.last_name}
                  </span>
                  <FaTimes size={10} style={{ color:T.gray, cursor:"pointer", WebkitTapHighlightColor:"transparent", marginLeft:4 }}
                    onClick={()=>handleSelect(null)}/>
                </div>
                <button onClick={()=>handleSelect(null)}
                  style={{ fontSize:11,fontWeight:700,color:T.blue,background:T.glass,border:T.glassBdr,borderRadius:8,padding:"6px 12px",cursor:"pointer", WebkitTapHighlightColor:"transparent",fontFamily:fontFam }}>
                  Change Student
                </button>
              </div>
            )
          }
        </div>
      </div>

      {/* ── Empty state ── */}
      {!selectedStudent && (
        <div style={{ textAlign:"center", padding:"60px 24px", color:T.gray }}>
          <Icon size={40} style={{ opacity:0.10, display:"block", margin:"0 auto 16px" }}/>
          <p style={{ fontSize:14, fontFamily:fontFam, margin:0 }}>
            Search and select a student above to load their {title.toLowerCase()}.
          </p>
        </div>
      )}

      {/* ── Loading ── */}
      {selectedStudent && loading && (
        <div style={{ textAlign:"center", padding:60, color:T.gray, fontSize:14, fontFamily:fontFam }}>Loading…</div>
      )}

      {/* ── Error ── */}
      {selectedStudent && !loading && error && (
        <div style={{ padding:"14px 18px",borderRadius:12,background:"rgba(248,215,218,0.92)",border:"1px solid #f3b8bc",color:"#721c24",fontSize:13,fontFamily:fontFam,display:"flex",alignItems:"center",gap:8 }}>
          <FaExclamationCircle size={14}/> {error}
        </div>
      )}

      {/* ── Content ── */}
      {selectedStudent && !loading && !error && dashboard && (
        mode === "dashboard"
          ? <PerformanceReportTab dashboard={dashboard}/>
          : <ProgressSummaryTab   dashboard={dashboard}/>
      )}
    </div>
  );
}
